import { getAuth } from 'firebase/auth';
import type { SocialEngagementQueueItem, SocialListeningQuery } from '@modules/shared-types';

function apiBase(): string {
  if (import.meta.env.VITE_AGENT_API_URL) return import.meta.env.VITE_AGENT_API_URL;
  if (import.meta.env.VITE_BUILDER_API_URL) return import.meta.env.VITE_BUILDER_API_URL;
  return window.location.hostname === 'localhost' ? 'http://localhost:8081' : 'https://api.segmently.ai';
}

export class SocialEngagementService {
  constructor(private readonly baseUrl = apiBase()) {}

  async listQueries(projectId: string): Promise<SocialListeningQuery[]> {
    const data = await this.request<{ queries: SocialListeningQuery[] }>(projectId, '/x/listening/queries');
    return data.queries;
  }

  async searchXUsers(projectId: string, body: Record<string, unknown>) {
    return this.request(projectId, '/x/users/search', { method: 'POST', body });
  }

  async importXAccount(projectId: string, username: string, body: Record<string, unknown>) {
    return this.request(projectId, `/x/accounts/${encodeURIComponent(username)}/import`, { method: 'POST', body });
  }

  async saveQuery(projectId: string, body: Record<string, unknown>) {
    return this.request(projectId, '/x/listening/queries', { method: 'POST', body });
  }

  async refreshQuery(projectId: string, queryId: string, body: Record<string, unknown>) {
    return this.request(projectId, `/x/listening/queries/${encodeURIComponent(queryId)}/refresh`, { method: 'POST', body });
  }

  async listEngagement(projectId: string, status?: string): Promise<SocialEngagementQueueItem[]> {
    const params = new URLSearchParams({ limit: '100' });
    if (status) params.set('status', status);
    const data = await this.request<{ items: SocialEngagementQueueItem[] }>(projectId, `/engagement?${params}`);
    return data.items;
  }

  async analyticsComparison(projectId: string, groupBy: 'pillar' | 'series' | 'audience' | 'post_type') {
    return this.request<{ groups: Array<{ key: string; publicationCount: number; metrics: Record<string, number>; engagementRate?: number }>; publicationsAnalyzed: number }>(
      projectId,
      `/x/analytics/compare?groupBy=${encodeURIComponent(groupBy)}`,
    );
  }

  async approve(projectId: string, itemId: string, text: string) {
    return this.request(projectId, `/engagement/${encodeURIComponent(itemId)}/approve`, { method: 'POST', body: { text } });
  }

  async dismiss(projectId: string, itemId: string) {
    return this.request(projectId, `/engagement/${encodeURIComponent(itemId)}/dismiss`, { method: 'POST', body: {} });
  }

  async reply(projectId: string, itemId: string, body: Record<string, unknown>) {
    return this.request(projectId, `/engagement/${encodeURIComponent(itemId)}/reply-x`, { method: 'POST', body });
  }

  async reconcileReply(projectId: string, itemId: string, body: Record<string, unknown>) {
    return this.request(projectId, `/engagement/${encodeURIComponent(itemId)}/reconcile-x`, { method: 'POST', body });
  }

  async approvePublication(projectId: string, strategyId: string, publicationId: string) {
    return this.publicationRequest(projectId, strategyId, publicationId, 'approve', {});
  }

  async publishPublication(projectId: string, strategyId: string, publicationId: string, body: Record<string, unknown>) {
    return this.publicationRequest(projectId, strategyId, publicationId, 'publish-x', body);
  }

  async reconcilePublication(projectId: string, strategyId: string, publicationId: string, body: Record<string, unknown>) {
    return this.publicationRequest(projectId, strategyId, publicationId, 'reconcile-x', body);
  }

  async refreshPublicationAnalytics(projectId: string, strategyId: string, publicationId: string, body: Record<string, unknown>) {
    return this.publicationRequest(projectId, strategyId, publicationId, 'analytics-x', body);
  }

  private publicationRequest(projectId: string, strategyId: string, publicationId: string, action: string, body: Record<string, unknown>) {
    return this.request(
      projectId,
      `/strategies/${encodeURIComponent(strategyId)}/publications/${encodeURIComponent(publicationId)}/${action}`,
      { method: 'POST', body },
    );
  }

  private async request<T = Record<string, unknown>>(
    projectId: string,
    path: string,
    options: { method?: 'GET' | 'POST'; body?: unknown } = {},
  ): Promise<T> {
    const token = await getAuth().currentUser?.getIdToken();
    if (!token) throw new Error('User not authenticated');
    const response = await fetch(
      `${this.baseUrl}/api/cli/v1/projects/${encodeURIComponent(projectId)}/content-plan${path}`,
      {
        method: options.method ?? 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        },
        body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      },
    );
    const payload = await response.json().catch(() => null) as { data?: T; detail?: string; message?: string; error?: string } | null;
    if (!response.ok) {
      throw new Error(payload?.detail ?? payload?.message ?? payload?.error ?? `Request failed: HTTP ${response.status}`);
    }
    return (payload?.data ?? payload) as T;
  }
}
